import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'phosphor-react';
import maplibregl, { Map as MLMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

type CoverageRow = {
  dept_code: string;
  dept_name: string;
  population: number;
  total_events: number;
  events_per_100k: number;
  pct_datatourisme: number;
  pct_openagenda: number;
  pct_ics: number;
  pct_autres: number;
  freshness_days: number | null;
};

const STYLE = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';
const GEOJSON_URL = 'https://cdn.jsdelivr.net/gh/gregoiredavid/france-geojson@master/departements.geojson';

// Échelle de couleurs (rouge = déficit, vert = bonne couverture).
function colorFor(value: number, median: number): string {
  if (value === 0) return '#9ca3af';            // gris
  const ratio = value / Math.max(median, 0.1);
  if (ratio < 0.33) return '#dc2626';           // rouge
  if (ratio < 0.66) return '#f97316';           // orange
  if (ratio < 1.0)  return '#facc15';           // jaune
  if (ratio < 1.5)  return '#84cc16';           // vert clair
  return '#16a34a';                             // vert foncé
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export default function AdminCoverage() {
  const nav = useNavigate();
  const { profile, loading } = useAuth();
  const [rows, setRows] = useState<CoverageRow[]>([]);
  const [selected, setSelected] = useState<CoverageRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const med = useMemo(() => median(rows.map((r) => r.events_per_100k)), [rows]);
  const byCode = useMemo(() => new Map(rows.map((r) => [r.dept_code, r])), [rows]);

  // Chargement des données de couverture.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('mv_coverage_by_dept')
        .select('*')
        .order('dept_code', { ascending: true });
      if (cancelled) return;
      if (error) setError(error.message);
      else setRows((data ?? []) as CoverageRow[]);
    })();
    return () => { cancelled = true; };
  }, []);

  // Initialise la carte MapLibre + couche choroplèthe.
  useEffect(() => {
    if (!containerRef.current || mapRef.current || !rows.length) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE,
      center: [2.4, 46.7],
      zoom: 4.8,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

    map.on('load', async () => {
      let geo: any;
      try {
        const res = await fetch(GEOJSON_URL);
        geo = await res.json();
      } catch (e) {
        console.error('[coverage] geojson fetch failed', e);
        return;
      }
      // Enrichit chaque feature avec la donnée de couverture pour l'expression de couleur.
      for (const f of geo.features ?? []) {
        const code = f.properties?.code as string | undefined;
        const r = code ? byCode.get(code) : undefined;
        f.properties = {
          ...f.properties,
          events_per_100k: r?.events_per_100k ?? 0,
          fill_color: colorFor(r?.events_per_100k ?? 0, med),
        };
      }
      map.addSource('depts', { type: 'geojson', data: geo });
      map.addLayer({
        id: 'depts-fill',
        type: 'fill',
        source: 'depts',
        paint: {
          'fill-color': ['get', 'fill_color'],
          'fill-opacity': 0.65,
        },
      });
      map.addLayer({
        id: 'depts-line',
        type: 'line',
        source: 'depts',
        paint: { 'line-color': '#1f2937', 'line-width': 0.5, 'line-opacity': 0.4 },
      });
      map.on('click', 'depts-fill', (ev) => {
        const code = ev.features?.[0]?.properties?.code as string | undefined;
        if (code) setSelected(byCode.get(code) ?? null);
      });
      map.on('mouseenter', 'depts-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'depts-fill', () => { map.getCanvas().style.cursor = ''; });
    });

    return () => { map.remove(); mapRef.current = null; };
  }, [rows, byCode, med]);

  if (loading) return <div className="p-6 text-ink-2">Chargement…</div>;
  if (!profile || profile.role !== 'admin') {
    return (
      <div className="p-6">
        <p className="t-ed-lg mb-2">Accès refusé</p>
        <p className="text-ink-2">Cette page est réservée aux administrateurs.</p>
      </div>
    );
  }

  const sorted = [...rows].sort((a, b) => a.events_per_100k - b.events_per_100k);
  const empty = rows.filter((r) => r.total_events === 0).length;
  const total = rows.reduce((a, r) => a + r.total_events, 0);

  return (
    <div className="bg-paper text-ink-1 min-h-screen pb-tabbar">
      <header className="px-5 pt-[max(env(safe-area-inset-top),12px)] pb-3 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="h-9 w-9 grid place-items-center rounded-full bg-paper-elev ring-1 ring-paper-edge"><ArrowLeft size={16} /></button>
        <span className="t-ed-lg">Couverture par département</span>
      </header>

      <div className="px-5 mb-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Départements" value={rows.length} />
        <Stat label="Sans aucun event" value={empty} highlight={empty > 0} />
        <Stat label="Events futurs (90j)" value={total.toLocaleString('fr-FR')} />
        <Stat label="Médiane events/100k" value={med.toFixed(1)} />
      </div>

      {error ? <p className="px-5 text-red-600 text-sm mb-2">Erreur : {error}</p> : null}

      <div className="px-5">
        <div ref={containerRef} className="w-full h-[420px] rounded-2xl overflow-hidden ring-1 ring-paper-edge" />
        <Legend median={med} />
      </div>

      {selected && (
        <div className="px-5 mt-4">
          <div className="rounded-2xl bg-paper-elev ring-1 ring-paper-edge p-4">
            <div className="flex items-baseline justify-between mb-2">
              <span className="t-ed-lg">{selected.dept_name} ({selected.dept_code})</span>
              <button onClick={() => setSelected(null)} className="text-ink-2 text-xs">fermer</button>
            </div>
            <p className="text-ink-2 text-sm mb-2">
              Population : {selected.population.toLocaleString('fr-FR')} hab.
            </p>
            <p className="mb-1">
              <strong>{selected.total_events}</strong> events futurs ·
              <strong> {selected.events_per_100k}</strong> e/100k hab
              {' '}(médiane nationale {med.toFixed(1)})
            </p>
            <p className="text-sm text-ink-2">
              DATAtourisme {selected.pct_datatourisme}% · OpenAgenda/ODS {selected.pct_openagenda}% ·
              ICS {selected.pct_ics}% · autres {selected.pct_autres}%
            </p>
            <p className="text-sm text-ink-2 mt-1">
              Dernière sync : {selected.freshness_days != null ? `il y a ${selected.freshness_days} j` : 'n/a'}
            </p>
          </div>
        </div>
      )}

      <section className="px-5 mt-6">
        <h2 className="t-ed-md mb-2">Top 25 en déficit relatif</h2>
        <div className="rounded-2xl ring-1 ring-paper-edge overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-paper-elev text-ink-2">
              <tr>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">Département</th>
                <th className="text-right px-3 py-2">Events</th>
                <th className="text-right px-3 py-2">e/100k</th>
                <th className="text-right px-3 py-2">%DT</th>
                <th className="text-right px-3 py-2">%OA</th>
                <th className="text-right px-3 py-2">Frais.</th>
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, 25).map((r) => (
                <tr key={r.dept_code}
                    className="border-t border-paper-edge cursor-pointer hover:bg-paper-elev"
                    onClick={() => setSelected(r)}>
                  <td className="px-3 py-2">{r.dept_code}</td>
                  <td className="px-3 py-2">{r.dept_name}</td>
                  <td className="px-3 py-2 text-right">{r.total_events}</td>
                  <td className="px-3 py-2 text-right">{r.events_per_100k}</td>
                  <td className="px-3 py-2 text-right">{r.pct_datatourisme}%</td>
                  <td className="px-3 py-2 text-right">{r.pct_openagenda}%</td>
                  <td className="px-3 py-2 text-right">{r.freshness_days ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className={`rounded-2xl p-3 ring-1 ring-paper-edge ${highlight ? 'bg-red-50' : 'bg-paper-elev'}`}>
      <div className="text-xs text-ink-2">{label}</div>
      <div className="t-ed-md">{value}</div>
    </div>
  );
}

function Legend({ median }: { median: number }) {
  const buckets = [
    { c: '#9ca3af', l: 'Aucun event' },
    { c: '#dc2626', l: `< ${(median * 0.33).toFixed(0)} e/100k` },
    { c: '#f97316', l: `< ${(median * 0.66).toFixed(0)}` },
    { c: '#facc15', l: `< ${median.toFixed(0)}` },
    { c: '#84cc16', l: `< ${(median * 1.5).toFixed(0)}` },
    { c: '#16a34a', l: `≥ ${(median * 1.5).toFixed(0)}` },
  ];
  return (
    <div className="flex flex-wrap gap-2 mt-2 text-xs text-ink-2">
      {buckets.map((b) => (
        <span key={b.l} className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded" style={{ background: b.c }} />
          {b.l}
        </span>
      ))}
    </div>
  );
}
